import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore api.js has no declaration file
import { getMydrawSurfaceBaseList } from '@src/api/map/api';
import {
    SurfaceType,
    type LayerSurfaceType,
    type ProjectSurfaceType,
    type SurfaceBaseByType,
    type SurfaceBaseItem,
    type SurfaceBaseList,
    type SurfaceZoomDevice,
    type SurfaceZoomRange,
} from '@src/api/map/types';

/** lite 控规 pipeline 启用的项目 surfaceType */
export const LITE_PROJECT_SURFACE_TYPES: ProjectSurfaceType[] = [SurfaceType.NEW_HOUSE, SurfaceType.SECOND_HAND_HOUSE, SurfaceType.SCHOOL];

/** 接口列表按 surfaceType 建索引，跳过 PUBLICFACILITIES */
const buildSurfaceBaseByType = (list: SurfaceBaseList): SurfaceBaseByType => {
    const byType: SurfaceBaseByType = {};
    for (const item of list) {
        if (item.surfaceType === SurfaceType.PUBLICFACILITIES) continue;
        byType[item.surfaceType] = item;
    }
    return byType;
};

/** 读取面 / 图层整体可见 zoom 区间 */
export const getSurfaceLayerZoomRange = (item: SurfaceBaseItem | undefined, device: SurfaceZoomDevice = 'desktop'): SurfaceZoomRange | null => {
    if (!item) return null;
    return {
        minZoom: device === 'desktop' ? item.minZoomLevel : item.padMinZoomLevel,
        maxZoom: device === 'desktop' ? item.maxZoomLevel : item.padMaxZoomLevel,
    };
};

/** 读取标签 zoom 区间（InfoWindow / textLabel） */
export const getSurfaceLabelZoomRange = (item: SurfaceBaseItem | undefined, device: SurfaceZoomDevice = 'desktop'): SurfaceZoomRange | null => {
    if (!item) return null;
    return {
        minZoom: device === 'desktop' ? item.labelMinZoomLevel : item.padMinZoomLevel,
        maxZoom: device === 'desktop' ? item.labelMaxZoomLevel : item.padMaxZoomLevel,
    };
};

/** 控规项目 label 全局最小 zoom（取 LITE_PROJECT_SURFACE_TYPES 的 labelMinZoomLevel 最小值） */
export const getProjectMinLabelZoom = (byType: SurfaceBaseByType, device: SurfaceZoomDevice = 'desktop'): number => {
    const levels = LITE_PROJECT_SURFACE_TYPES.map(type => {
        const item = byType[type];
        if (!item) return undefined;
        return device === 'desktop' ? item.labelMinZoomLevel : item.padMinZoomLevel;
    }).filter((v): v is number => Number.isFinite(v));

    return levels.length > 0 ? Math.min(...levels) : 13.5;
};

export const useStoreSurfaceBase = defineStore('surfaceBase', () => {
    /** surfaceBase/list 原始列表，地图渲染配置唯一数据来源 */
    const surfaceBaseList = shallowRef<SurfaceBaseList>([]);
    /** 按 surfaceType 索引，已排除 PUBLICFACILITIES */
    const surfaceBaseByType = shallowRef<SurfaceBaseByType>({});

    const setSurfaceBaseList = (list: SurfaceBaseList) => {
        surfaceBaseList.value = list;
        surfaceBaseByType.value = buildSurfaceBaseByType(list);
    };

    /** 拉取并写入地图渲染配置（首页 initialize 必调，换城市后需重新拉取） */
    const fetchSurfaceBaseList = async (mapId: string | number) => {
        const { data = [] } = await getMydrawSurfaceBaseList({ mapId });
        const list = Array.isArray(data) ? data : [];
        setSurfaceBaseList(list);
        return list;
    };

    const getSurfaceConfig = (surfaceType: SurfaceType): SurfaceBaseItem | undefined => surfaceBaseByType.value[surfaceType];

    const getLayerZoomRange = (surfaceType: LayerSurfaceType, device: SurfaceZoomDevice = 'desktop') =>
        getSurfaceLayerZoomRange(getSurfaceConfig(surfaceType), device);

    /** 登出时清空内存缓存 */
    const resetSurfaceBaseState = () => {
        surfaceBaseList.value = [];
        surfaceBaseByType.value = {};
    };

    return {
        surfaceBaseList,
        surfaceBaseByType,
        setSurfaceBaseList,
        fetchSurfaceBaseList,
        getSurfaceConfig,
        getLayerZoomRange,
        resetSurfaceBaseState,
    };
});
